/**
 * When to auto-move the Explore floor camera.
 *
 * Full fit / pin-focus zoom only on the first paint for a casino map.
 * After that, insight/timeframe (and other EGM) reloads keep the user's zoom
 * but may smoothly pan to the new rank-1 pin. Explicit locate (recenter
 * control) and pin restore are handled outside this helper.
 */

export const TOP_PIN_PAN_DURATION_MS = 500;

/** Same increment as MapControls `map.zoomIn()` (Mapbox default). */
export const MAP_ZOOM_CONTROL_STEP = 1;

/** Extra zoom-control steps after the first #1 pin focus on casino load. */
export const FIRST_LOAD_EXTRA_ZOOM_STEPS = 2;

/** Smooth extra zoom after the first #1 pin focus on casino load. */
export const FIRST_LOAD_EXTRA_ZOOM_DURATION_MS = 500;

/**
 * Zoom level after the initial top-pin focus: +N MapControls steps
 * (default two), clamped to the map's max zoom.
 */
export function resolveFirstLoadZoomAfterTopPin(input: {
  focusZoom: number;
  maxZoom: number;
  step?: number;
}): number {
  const step = input.step ?? FIRST_LOAD_EXTRA_ZOOM_STEPS * MAP_ZOOM_CONTROL_STEP;
  return Math.min(input.focusZoom + step, input.maxZoom);
}

/** Initial-load / casino-change: fit or focus-with-zoom. */
export function shouldAutoFitMapCamera(input: {
  initialFitDone: boolean;
  pinSelected: boolean;
  didFlyToIndividualSlot: boolean;
  markerCount: number;
}): boolean {
  if (input.pinSelected || input.didFlyToIndividualSlot) return false;
  if (input.markerCount <= 0) return false;
  return !input.initialFitDone;
}

/**
 * After the floor camera is established, pan to the new #1 pin on EGM reload
 * (insight/timeframe) without changing zoom.
 */
export function shouldPanToTopPinOnDataReload(input: {
  initialFitDone: boolean;
  pinSelected: boolean;
  didFlyToIndividualSlot: boolean;
  markerCount: number;
}): boolean {
  if (!input.initialFitDone) return false;
  if (input.pinSelected || input.didFlyToIndividualSlot) return false;
  return input.markerCount > 0;
}

/**
 * Zoom for a top-pin pan: keep the user's zoom unless the pin cannot be
 * centered at that zoom (e.g. outside max bounds), in which case use fallback.
 */
export function resolveTopPinPanZoom(input: {
  currentZoom: number;
  canCenterAtCurrentZoom: boolean;
  fallbackZoom: number;
}): number {
  if (input.canCenterAtCurrentZoom) return input.currentZoom;
  return input.fallbackZoom;
}

/** Recenter an empty floor only before the initial camera has been established. */
export function shouldRecenterEmptyMap(input: {
  initialFitDone: boolean;
  pinSelected: boolean;
  isIndividualFocus: boolean;
  hasHotMarkers: boolean;
  hasColdMarkers: boolean;
}): boolean {
  if (input.isIndividualFocus || input.pinSelected) return false;
  if (input.hasHotMarkers || input.hasColdMarkers) return false;
  return !input.initialFitDone;
}
